import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const TeacherPreferences = ({ teacher, onClose }) => {
  const { showSuccess, showError } = useNotificationContext();
  const [activeTab, setActiveTab] = useState('preferences');
  const [preferences, setPreferences] = useState({
    preferredDays: [],
    preferredTimeSlots: [],
    maxHoursPerDay: 6,
    maxHoursPerWeek: 20,
    unavailableSlots: [],
    lunchBreakRequired: true,
    consecutiveHoursMax: 4,
    preferredCourses: []
  });
  const [allCourses, setAllCourses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [courseFilters, setCourseFilters] = useState({ academicYear: '', department: '', semester: 1, track: '' });
  const [loading, setLoading] = useState(false);

  const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const timeSlots = ['8h30-10h00', '10h10-11h40', '11h50-13h20', '13h30-15h00', '15h10-16h40', '16h50-18h20'];

  useEffect(() => {
    fetchPreferences();
    fetchCourses();
    fetchAcademicYears();
    fetchTracks();
  }, [teacher._id]);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-schedule/teacher-preferences/${teacher._id}`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPreferences(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Erreur chargement préférences:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses`);
      setAllCourses(response.data);
    } catch (error) {
      console.error('Erreur chargement cours:', error);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/academic-years`);
      setAcademicYears(response.data);
    } catch (error) {
      console.error('Erreur chargement années:', error);
    }
  };

  const fetchTracks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tracks`);
      setTracks(response.data);
    } catch (error) {
      console.error('Erreur chargement parcours:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/ai-schedule/teacher-preferences/${teacher._id}`, preferences);
      showSuccess('Préférences sauvegardées');
      onClose();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showError('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (dayIndex) => {
    const newDays = preferences.preferredDays.includes(dayIndex + 1)
      ? preferences.preferredDays.filter(d => d !== dayIndex + 1)
      : [...preferences.preferredDays, dayIndex + 1];
    setPreferences({ ...preferences, preferredDays: newDays });
  };

  const toggleTimeSlot = (slotIndex) => {
    const newSlots = preferences.preferredTimeSlots.includes(slotIndex + 1)
      ? preferences.preferredTimeSlots.filter(s => s !== slotIndex + 1)
      : [...preferences.preferredTimeSlots, slotIndex + 1];
    setPreferences({ ...preferences, preferredTimeSlots: newSlots });
  };

  const toggleUnavailableSlot = (day, timeSlot) => {
    const key = `${day}_${timeSlot}`;
    const exists = preferences.unavailableSlots.some(slot => slot.day === day && slot.timeSlot === timeSlot);
    
    if (exists) {
      setPreferences({
        ...preferences,
        unavailableSlots: preferences.unavailableSlots.filter(slot => !(slot.day === day && slot.timeSlot === timeSlot))
      });
    } else {
      setPreferences({
        ...preferences,
        unavailableSlots: [...preferences.unavailableSlots, { day, timeSlot }]
      });
    }
  };

  const isUnavailable = (day, timeSlot) => {
    return preferences.unavailableSlots.some(slot => slot.day === day && slot.timeSlot === timeSlot);
  };

  const toggleCourse = (courseId) => {
    const newCourses = preferences.preferredCourses.includes(courseId)
      ? preferences.preferredCourses.filter(id => id !== courseId)
      : [...preferences.preferredCourses, courseId];
    setPreferences({ ...preferences, preferredCourses: newCourses });
  };

  const handleCourseFilterChange = (field, value) => {
    const newFilters = { ...courseFilters, [field]: value };
    if (field === 'department' || field === 'academicYear') {
      newFilters.track = '';
    }
    setCourseFilters(newFilters);
  };

  const getFilteredCourses = () => {
    return allCourses.filter(course => {
      const matchesYear = !courseFilters.academicYear || 
        (course.academicYear && course.academicYear._id === courseFilters.academicYear);
      const matchesDept = !courseFilters.department || 
        (course.department && course.department._id === courseFilters.department);
      const matchesSemester = course.semester === courseFilters.semester;
      const matchesTrack = !courseFilters.track || 
        (course.track && course.track._id === courseFilters.track);
      return matchesYear && matchesDept && matchesSemester && matchesTrack;
    });
  };

  const getSelectedCourses = () => {
    return allCourses.filter(course => preferences.preferredCourses.includes(course._id));
  };

  const getFilteredTracks = () => {
    if (!courseFilters.department || !courseFilters.academicYear) {
      return tracks;
    }
    const relevantTrackIds = new Set(
      allCourses
        .filter(course => 
          course.department && course.department._id === courseFilters.department &&
          course.academicYear && course.academicYear._id === courseFilters.academicYear &&
          course.semester === courseFilters.semester
        )
        .map(course => course.track && course.track._id)
        .filter(Boolean)
    );
    return tracks.filter(track => relevantTrackIds.has(track._id));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-medium">Préférences - {teacher.firstName} {teacher.lastName}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">×</button>
        </div>

        {/* Onglets */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('preferences')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'preferences'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Préférences Horaires
            </button>
            <button
              onClick={() => setActiveTab('courses')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'courses'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Cours Préférés
            </button>
          </nav>
        </div>

        <div className="p-6 space-y-6">
          {activeTab === 'preferences' && (
            <>
              {/* Jours préférés */}
              <div>
                <h4 className="font-medium mb-3">Jours préférés</h4>
                <div className="grid grid-cols-3 gap-2">
                  {days.map((day, index) => (
                    <button
                      key={index}
                      onClick={() => toggleDay(index)}
                      className={`p-2 text-sm rounded border ${
                        preferences.preferredDays.includes(index + 1)
                          ? 'bg-primary-100 border-primary-300 text-primary-800'
                          : 'bg-gray-50 border-gray-300 text-gray-700'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Créneaux préférés */}
              <div>
                <h4 className="font-medium mb-3">Créneaux horaires préférés</h4>
                <div className="grid grid-cols-2 gap-2">
                  {timeSlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() => toggleTimeSlot(index)}
                      className={`p-2 text-sm rounded border ${
                        preferences.preferredTimeSlots.includes(index + 1)
                          ? 'bg-primary-100 border-primary-300 text-primary-800'
                          : 'bg-gray-50 border-gray-300 text-gray-700'
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Limites */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max heures/jour</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={preferences.maxHoursPerDay}
                    onChange={(e) => setPreferences({...preferences, maxHoursPerDay: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max heures/semaine</label>
                  <input
                    type="number"
                    min="1"
                    max="40"
                    value={preferences.maxHoursPerWeek}
                    onChange={(e) => setPreferences({...preferences, maxHoursPerWeek: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max heures consécutives</label>
                  <input
                    type="number"
                    min="1"
                    max="6"
                    value={preferences.consecutiveHoursMax}
                    onChange={(e) => setPreferences({...preferences, consecutiveHoursMax: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={preferences.lunchBreakRequired}
                      onChange={(e) => setPreferences({...preferences, lunchBreakRequired: e.target.checked})}
                      className="mr-2"
                    />
                    Pause déjeuner requise
                  </label>
                </div>
              </div>

              {/* Créneaux indisponibles */}
              <div>
                <h4 className="font-medium mb-3">Créneaux indisponibles</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-left p-1">Horaire</th>
                        {days.map((day, dayIndex) => (
                          <th key={dayIndex} className="text-center p-1 min-w-16">{day.slice(0, 3)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {timeSlots.map((slot, slotIndex) => (
                        <tr key={slotIndex}>
                          <td className="p-1 font-medium">{slot}</td>
                          {days.map((day, dayIndex) => (
                            <td key={dayIndex} className="p-1 text-center">
                              <button
                                onClick={() => toggleUnavailableSlot(dayIndex + 1, slotIndex + 1)}
                                className={`w-8 h-8 rounded text-xs ${
                                  isUnavailable(dayIndex + 1, slotIndex + 1)
                                    ? 'bg-red-200 text-red-800'
                                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                                }`}
                              >
                                {isUnavailable(dayIndex + 1, slotIndex + 1) ? '❌' : '✅'}
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === 'courses' && (
            <div className="space-y-6">
              {/* Cours sélectionnés */}
              {getSelectedCourses().length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Cours sélectionnés ({getSelectedCourses().length})</h4>
                  <div className="grid gap-2">
                    {getSelectedCourses().map(course => (
                      <div key={course._id} className="flex items-center justify-between p-3 bg-primary-50 rounded-lg">
                        <div>
                          <span className="font-medium">{course.name}</span>
                          <span className="text-sm text-gray-500 ml-2">({course.code})</span>
                          <div className="text-xs text-gray-500">
                            {course.department?.name} | {course.academicYear?.name} | S{course.semester} | {course.track?.name}
                          </div>
                        </div>
                        <button
                          onClick={() => toggleCourse(course._id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtres */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
                  <select 
                    value={courseFilters.academicYear} 
                    onChange={(e) => handleCourseFilterChange('academicYear', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value="">Toutes les années</option>
                    {academicYears.map(year => (
                      <option key={year._id} value={year._id}>{year.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
                  <select 
                    value={courseFilters.department} 
                    onChange={(e) => handleCourseFilterChange('department', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value="">Tous les départements</option>
                    {teacher?.departments?.map(dept => (
                      <option key={dept._id} value={dept._id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
                  <select 
                    value={courseFilters.semester} 
                    onChange={(e) => handleCourseFilterChange('semester', parseInt(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value={1}>Semestre 1</option>
                    <option value={2}>Semestre 2</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parcours</label>
                  <select 
                    value={courseFilters.track} 
                    onChange={(e) => handleCourseFilterChange('track', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value="">Tous les parcours</option>
                    {getFilteredTracks().map(track => (
                      <option key={track._id} value={track._id}>{track.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Liste des cours */}
              <div>
                <h4 className="font-medium mb-3">Cours disponibles ({getFilteredCourses().length})</h4>
                {getFilteredCourses().length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📚</div>
                    <p>Aucun cours trouvé avec les filtres sélectionnés</p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {getFilteredCourses().map(course => (
                      <label key={course._id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={preferences.preferredCourses.includes(course._id)}
                          onChange={() => toggleCourse(course._id)}
                          className="mr-3"
                        />
                        <div className="flex-1">
                          <span className="font-medium">{course.name}</span>
                          <span className="text-sm text-gray-500 ml-2">({course.code})</span>
                          <div className="text-xs text-gray-500">
                            Cours: {course.hours?.lectures || 0}h | TD: {course.hours?.tutorials || 0}h | TP: {course.hours?.practicals || 0}h
                          </div>
                          <div className="text-xs text-gray-400">
                            {course.department?.name} | {course.academicYear?.name} | {course.track?.name}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherPreferences;